import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import type { ContractApplyPartyResult, ContractApplyPreview, ContractExtensionOptions, ContractExtraction, ContractMilestone, ContractMoney, ContractPartyRole, ContractPropertyMatch, DealDeadline, DeadlineAnchor } from '../../types';
import { Button, Dialog } from '../ui';
import { DAY1_ANCHORS, resolveContractDates } from '../../utils/contractDates';
import { deadlineDate } from '../../utils/deadlineDates';

interface Props {
  dealId: string;
  onClose: () => void;
  onApplied: () => void;
}

// A party row in the review, with the two apply flags. DOCTRACK: a broker's brokerage and phone.
interface PartyRow { role: ContractPartyRole; name: string; email: string; firm: string; phone: string; link: boolean; correct_name: boolean; }

const PARTY_ROLES: ContractPartyRole[] = ['buyer', 'seller', 'buyers_broker', 'listing_broker', 'escrow', 'title', 'lender'];
const ROLE_LABEL: Record<ContractPartyRole, string> = {
  buyer: 'buyer', seller: 'seller', buyers_broker: "buyer's broker", listing_broker: 'listing broker', escrow: 'escrow', title: 'title', lender: 'lender',
};
const isBroker = (r: ContractPartyRole) => r === 'buyers_broker' || r === 'listing_broker';
const NAME_ONLY = 'not linked - only the name matched';

/** "Read on Nov 1": the Pacific day a kept read was made. */
const readOnWords = (iso: string): string =>
  `Read on ${new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })}`;

/** ", notice 5 days before it ends" - or "by the day it ends" for 0; business days when the contract says so. */
const beforeEndWords = (what: string, n: number | null | undefined, unit: ContractExtensionOptions['notice_day_unit']): string => {
  if (n == null) return '';
  if (n === 0) return `, ${what} by the day it ends`;
  const days = `${unit === 'business' ? 'business ' : ''}day${n === 1 ? '' : 's'}`;
  return `, ${what} ${n} ${days} before it ends`;
};
const EMPTY_MONEY: ContractMoney = { purchase_price: null, emd_amount: null, loan_amount: null, commission_percent: null };
const MONEY_KEYS: ReadonlyArray<keyof ContractMoney> = ['purchase_price', 'emd_amount', 'loan_amount', 'commission_percent'];
/** A money figure as he reads it: "$50,000", or "3%" for the commission. */
const moneyWords = (key: keyof ContractMoney, n: number): string =>
  (key === 'commission_percent' ? `${n}%` : `$${n.toLocaleString('en-US')}`);

/** Whole calendar days from the escrow open date to a date - what a re-anchor shifts a contract row by. */
const daysFrom = (anchor: string, date: string): number =>
  Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${anchor.slice(0, 10)}T00:00:00Z`)) / 86400000);

/**
 * Each row's own key (the label it was READ with) - a chain names it, so renaming a row never breaks one.
 * DOCTRACK part 2 (design 4.5): `match` is the deal row it changes ('new' for a new row); `index` is its place in the
 * read's `timeline.milestones` (F2), kept when a row above it is removed.
 */
interface RowMeta {
  key: string; taken: number; match: string; index: number | null;
  /** His correction of what the row counts from (null: the clause's own). */
  anchor: DeadlineAnchor | null;
  /** What starts an event row's clock ("Title report"). */
  eventLabel: string | null;
}

const NEW_ROW = 'new';
/** What a row can count from, in his words, in this order. */
const ANCHOR_WORDS: ReadonlyArray<[DeadlineAnchor, string]> = [
  ['escrow_open', 'Day 1 (escrow open)'], ['effective_date', 'the effective date (Day 1)'],
  ['agreement_date', "the original agreement's date (Day 1)"], ['amendment_date', "this amendment's date"],
  ['event', 'an event'], ['explicit', 'a stated date'],
];
/** A row that counts from ANOTHER row keeps its chain's words - it has no anchor of its own to correct. */
const isChain = (m: ContractMilestone) => m.offset_from === 'milestone' || m.offset_from === 'contingency_removal';
/** What a row counts from by its own clause (or his correction) - never inherited through a chain. */
const ownAnchorOf = (m: ContractMilestone, fix: DeadlineAnchor | null | undefined): DeadlineAnchor | null => {
  if (fix) return fix;
  if (m.explicit_date) return 'explicit';
  if (m.offset_from === 'escrow_open' || m.offset_from === 'effective_date' || m.offset_from === 'agreement_date' || m.offset_from === 'amendment_date') return m.offset_from;
  return m.offset_from == null && m.offset_days != null ? 'event' : null;
};
/** The deal rows a contract can match or remove - never his own custom rows. */
const contractRowsOf = (p: ContractApplyPreview | null): DealDeadline[] =>
  (p?.existing ?? []).filter((r) => r.source === 'template' || r.source === 'contract');
const DEADLINES_UNLOADED = "Couldn't load this deal's deadlines - Apply waits until they load.";
const NOT_MARKED_APPLIED = 'Applied - but the contract could not be marked applied.';

const numOrNull = (v: string): number | null => { if (v.trim() === '') return null; const n = parseFloat(v.replace(/[$,%\s]/g, '')); return Number.isFinite(n) ? n : null; };

export default function ContractTimelineReview({ dealId, onClose, onApplied }: Props) {
  const [anchor, setAnchor] = useState('');
  const [milestones, setMilestones] = useState<ContractMilestone[]>([]);
  const [meta, setMeta] = useState<RowMeta[]>([]);
  const [weekendRoll, setWeekendRoll] = useState(false);
  const [money, setMoney] = useState<ContractMoney>(EMPTY_MONEY);
  const [parties, setParties] = useState<PartyRow[]>([]);
  const [property, setProperty] = useState<{ address: string | null; apn: string | null; city: string | null; state: string | null }>({ address: null, apn: null, city: null, state: null });
  const [propertyMatch, setPropertyMatch] = useState<ContractPropertyMatch | null>(null);
  const [linkProperty, setLinkProperty] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [extracted, setExtracted] = useState(false);
  const [savedDoc, setSavedDoc] = useState<string | null>(null);
  const [existingDocs, setExistingDocs] = useState<Array<{ id: string; file_name: string }>>([]);
  const [pickedDoc, setPickedDoc] = useState('');
  // DOCTRACK: the kept read behind this review - its filing (Apply records it), when it was read, and what it was read as.
  const [filingId, setFilingId] = useState<string | null>(null);
  const [readAt, setReadAt] = useState<string | null>(null);
  const [reused, setReused] = useState(false);
  const [asAmendment, setAsAmendment] = useState(false);
  const [readKind, setReadKind] = useState<'purchase_agreement' | 'amendment'>('purchase_agreement');
  const [lastSource, setLastSource] = useState<{ file: File } | { doc: string } | null>(null);
  // DOCTRACK: brokers Apply matched by name alone, waiting for his pick.
  const [asks, setAsks] = useState<Array<{ party: PartyRow; result: ContractApplyPartyResult }>>([]);
  const [askErr, setAskErr] = useState<string | null>(null);
  // DOCTRACK part 2: the review's first look at the deal. Apply waits until it answers.
  const [look, setLook] = useState<ContractApplyPreview | null>(null);
  const [lookFailed, setLookFailed] = useState(false);
  const [anchorTyped, setAnchorTyped] = useState(false);
  const [removeTicks, setRemoveTicks] = useState<Record<string, boolean>>({});
  const [notMarked, setNotMarked] = useState(false);
  const [amendmentDate, setAmendmentDate] = useState('');
  // DOCTRACK part 2: the read's own figures (the inputs stay editable), and which lines Apply writes.
  const [readMoney, setReadMoney] = useState<ContractMoney>(EMPTY_MONEY);
  const [moneyTicks, setMoneyTicks] = useState<Record<keyof ContractMoney, boolean>>({ purchase_price: false, emd_amount: false, loan_amount: false, commission_percent: false });

  // Offer documents already on the deal so the PSA doesn't have to be re-uploaded.
  useEffect(() => {
    api.getDealRoom(dealId)
      .then((r) => setExistingDocs(
        (r.documents || [])
          // A pending slot is a promise with no bytes - nothing to extract from.
          .filter((d: { status?: string; file_id?: string | null }) => d.status !== 'pending' && d.file_id)
          .map((d: { id: string; title?: string; file_name?: string | null }) => ({ id: d.id, file_name: d.title ?? d.file_name ?? 'document' }))
      ))
      .catch(() => {});
  }, [dealId]);

  /** Each row's match from the deal's first look: the deal row it changes, its extensions taken. */
  const matchedMeta = (p: ContractApplyPreview | null, rows: RowMeta[]): RowMeta[] =>
    rows.map((x, i) => {
      const u = p?.plan.updates.find((up) => up.incomingIndex === i);
      const deal = u ? contractRowsOf(p).find((r) => r.id === u.id) ?? u.before : null;
      // His earlier words on the deal row win over the read's own; a new row starts at the read's.
      return { ...x, match: u ? u.id : NEW_ROW, taken: deal ? deal.extensions_taken ?? 0 : x.taken, eventLabel: deal?.event_label ?? x.eventLabel };
    });

  /** The review's first look at the deal - null when it could not be read (Apply then waits, with "Try again"). */
  const lookAtDeal = async (ms: ContractMilestone[]): Promise<ContractApplyPreview | null> => {
    if (ms.length === 0) { setLook(null); setLookFailed(false); return null; }
    try {
      const p = await api.previewContractApply(dealId, ms.map(({ label, deadline_type }) => ({ label, deadline_type })));
      setLook(p); setLookFailed(false);
      setRemoveTicks(Object.fromEntries(p.plan.removals.map((r) => [r.id, r.ticked])));
      return p;
    } catch {
      setLook(null); setLookFailed(true);
      return null;
    }
  };

  const tryAgain = async () => {
    setBusy(true);
    const p = await lookAtDeal(milestones);
    if (p) {
      setMeta((xs) => matchedMeta(p, xs));
      if (!anchorTyped && p.escrow_opened_date) setAnchor(p.escrow_opened_date.slice(0, 10));
    }
    setBusy(false);
  };

  const applyResult = (res: ContractExtraction, p: ContractApplyPreview | null) => {
    setMilestones(res.timeline.milestones);
    setMeta(matchedMeta(p, res.timeline.milestones.map((m, i) => ({ key: m.label, taken: 0, match: NEW_ROW, index: i, anchor: null, eventLabel: m.event_label ?? null }))));
    setWeekendRoll(res.timeline.weekend_roll === true);
    setNotMarked(false);
    // DOCTRACK: Day 1 is the deal's own escrow open date when it has one. An amendment's own date is never Day 1
    // (reviewer finding 3) - he enters the deal's escrow open date.
    const dealDay1 = p?.escrow_opened_date ? p.escrow_opened_date.slice(0, 10) : '';
    const readDay1 = res.kind !== 'amendment' && res.timeline.effective_date ? res.timeline.effective_date.slice(0, 10) : '';
    setAnchor((a) => a || dealDay1 || readDay1);
    // An amendment's own date - the rows it counts from it, never escrow open.
    setAmendmentDate(res.kind === 'amendment' && res.timeline.effective_date ? res.timeline.effective_date.slice(0, 10) : '');
    setMoney(res.money || EMPTY_MONEY);
    setReadMoney(res.money || EMPTY_MONEY);
    // A line the contract states starts ticked; a Clear (the deal has a figure the contract does not) starts unticked.
    setMoneyTicks(Object.fromEntries(MONEY_KEYS.map((k) => [k, (res.money ?? EMPTY_MONEY)[k] != null])) as Record<keyof ContractMoney, boolean>);
    setParties((res.parties || []).map((p) => ({ role: p.role, name: p.name || '', email: p.email || '', firm: p.firm || '', phone: p.phone || '', link: true, correct_name: false })));
    setProperty(res.property || { address: null, apn: null, city: null, state: null });
    setPropertyMatch(res.property_match || null);
    if (res.document) setSavedDoc(res.document.file_name);
    setFilingId(res.filing_id ?? null);
    setReadAt(res.read_at ?? null);
    setReused(res.reused === true);
    setReadKind(res.kind === 'amendment' ? 'amendment' : 'purchase_agreement');
    setExtracted(true);
  };

  /** One read of a file or a document on the deal - as a purchase agreement, or as an amendment on his choice. */
  const read = async (source: { file: File } | { doc: string }, reread: boolean) => {
    setBusy(true); setErr(null);
    setLastSource(source);
    const opts = { kind: asAmendment ? 'amendment' as const : 'purchase_agreement' as const, reread };
    try {
      const res = 'file' in source
        ? await api.extractContractTimeline(dealId, source.file, opts)
        : await api.extractContractFromExisting(dealId, source.doc, opts);
      // DOCTRACK part 2: the deal's own rows first - the review renders once, with what each row matches.
      applyResult(res, await lookAtDeal(res.timeline.milestones));
    }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Extraction failed'); setExtracted(true); }
    finally { setBusy(false); }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await read({ file }, false);
  };

  const onExtractExisting = async () => {
    if (!pickedDoc) return;
    await read({ doc: pickedDoc }, false);
  };

  // ⭐ CR1-B: where each row LANDS - by the contract's own chain, business days, extensions taken and weekend roll.
  //   A row with nothing to count from gets no date, never escrow open + its days.
  // DOCTRACK part 2: each row by its own anchor - his correction, the amendment's own date, an event's recorded arrival.
  const eventDates: Record<number, string> = {};
  meta.forEach((x, i) => {
    const d = contractRowsOf(look).find((r) => r.id === x.match)?.event_date;
    if (d) eventDates[i] = d.slice(0, 10);
  });
  const resolved = resolveContractDates(
    milestones.map((m, i) => ({ ...m, label: meta[i]?.key ?? m.label })),
    anchor,
    {
      weekendRoll,
      extensionsTaken: Object.fromEntries(meta.map((x, i) => [i, x.taken])),
      anchors: Object.fromEntries(meta.flatMap((x, i) => (x.anchor ? [[i, x.anchor]] : []))),
      amendmentDate: amendmentDate || null,
      eventDates,
    },
  );
  /** Day 1 is needed only when a row counts from it. */
  const needsDay1 = resolved.some((r) => !!r.anchor && DAY1_ANCHORS.includes(r.anchor));
  const showAmendmentDate = readKind === 'amendment' || resolved.some((r) => r.anchor === 'amendment_date');
  const dateAt = (i: number): string => resolved[i]?.date ?? '';
  // DOCTRACK part 2: the deal rows no review row matches - removed only when he ticks them.
  const freed = contractRowsOf(look).filter((r) => !meta.some((x) => x.match === r.id));

  const apply = async () => {
    if (needsDay1 && !anchor) { setErr('Set the escrow open date (Day 1) first - it anchors every deadline.'); return; }
    if (!look) { setErr(DEADLINES_UNLOADED); return; }
    if (milestones.length === 0) { setErr('No deadlines to apply. Extract a contract, or add a deadline manually.'); return; }
    setBusy(true); setErr(null);
    try {
      const out = await api.applyContractTimeline(dealId, {
        // Sent only when set: an amendment counted from its own date needs no Day 1.
        ...(anchor ? { escrow_opened_date: anchor } : {}),
        // A row whose date ROOTS at escrow open carries its calendar offset (a re-anchor shifts it); a stated date, a
        // chain counted from one, and a row that needs a date carry none - re-setting escrow open must not move them.
        milestones: milestones.map((m, i) => ({
          label: m.label, deadline_type: m.deadline_type,
          offset_days: resolved[i]?.date && resolved[i].root === 'escrow' ? daysFrom(anchor, resolved[i].date!) : null,
          due_date: resolved[i]?.date ?? null,
          // The clause the reader took this row from, kept on the deadline so its morning card can quote the contract.
          source_text: m.source_text || null,
          // DOCTRACK part 2: the deal row it changes, the extensions taken, what it counts from and its day rule, and its
          // place in the read (F2) - the server takes the read itself from the filing, never from here.
          match_id: meta[i]?.match ?? NEW_ROW,
          extensions_taken: meta[i]?.taken ?? 0,
          anchor: resolved[i]?.anchor ?? null,
          day_unit: m.day_unit === 'business' ? 'business' as const : 'calendar' as const,
          weekend_roll: weekendRoll,
          // An event row keeps its own day count and what starts it; a chain from it is placed by a re-apply, never
          // counted from the arrival itself.
          ...(ownAnchorOf(m, meta[i]?.anchor) === 'event'
            ? { event_label: meta[i]?.eventLabel?.trim() || null, event_days: m.offset_days ?? null }
            : { event_label: null, event_days: null }),
          milestone_index: meta[i]?.index ?? null,
        })),
        remove_ids: freed.filter((r) => removeTicks[r.id] === true).map((r) => r.id),
        money,
        // Only the lines he left ticked are written; a line with no tick sends false.
        money_ticks: Object.fromEntries(MONEY_KEYS.map((k) => [k, moneyTick(k) !== null && moneyTicks[k]])) as Record<keyof ContractMoney, boolean>,
        parties: parties.map((p) => ({
          role: p.role, name: p.name.trim() || null, email: p.email.trim() || null, link: p.link, correct_name: p.correct_name,
          ...(isBroker(p.role) ? { firm: p.firm.trim() || null, phone: p.phone.trim() || null } : {}),
        })),
        property: !linkProperty ? { action: 'none' }
          : propertyMatch ? { action: 'link', property_id: propertyMatch.property_id }
          : property.address ? { action: 'create', address: property.address, apn: property.apn, city: property.city, state: property.state }
          : { action: 'none' },
        filing_id: filingId,
      });
      onApplied();
      // DOCTRACK part 2: the rows are written; a failed "applied" mark says so and the review stays open.
      const notRecorded = out?.applied_recorded === false;
      if (notRecorded) setNotMarked(true);
      // DOCTRACK: a broker matched by name alone is not linked; he picks before the review closes.
      const waiting = ((out?.parties ?? []) as ContractApplyPartyResult[]).filter((r) => r.action === NAME_ONLY && (r.candidates?.length ?? 0) > 0);
      if (waiting.length === 0) { if (!notRecorded) onClose(); return; }
      setAsks(waiting.map((result) => ({
        result,
        party: parties.find((p) => p.role === result.role && p.name.trim() === (result.name ?? '')) ?? { role: result.role as ContractPartyRole, name: result.name ?? '', email: '', firm: '', phone: '', link: true, correct_name: false },
      })));
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Apply failed'); }
    finally { setBusy(false); }
  };

  /** His answer for one broker: link the contact he picked, or make a new one. The review closes when none are left. */
  const answerAsk = async (i: number, answer: { link_contact_id: string } | { create: true }) => {
    const { party } = asks[i];
    setBusy(true); setAskErr(null);
    try {
      await api.applyContractParties(dealId, [{
        role: party.role, name: party.name.trim() || null, email: party.email.trim() || null,
        firm: party.firm.trim() || null, phone: party.phone.trim() || null, ...answer,
      }]);
      const left = asks.filter((_, k) => k !== i);
      setAsks(left);
      onApplied();
      if (left.length === 0) onClose();
    } catch (e2) { setAskErr(e2 instanceof Error ? e2.message : 'Could not link the contact'); }
    finally { setBusy(false); }
  };

  const patch = (i: number, p: Partial<ContractMilestone>) =>
    setMilestones((ms) => ms.map((m, idx) => idx === i ? { ...m, ...p } : m));
  const removeRow = (i: number) => {
    setMilestones((ms) => ms.filter((_, idx) => idx !== i));
    setMeta((xs) => xs.filter((_, idx) => idx !== i));
  };
  const setTaken = (i: number, taken: number) => setMeta((xs) => xs.map((x, idx) => (idx === i ? { ...x, taken } : x)));
  const patchMeta = (i: number, p: Partial<RowMeta>) => setMeta((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...p } : x)));
  /** His pick of the deal row a review row changes. A deal row another review row held moves that one to a new row. */
  const setMatch = (i: number, match: string) => {
    const deal = contractRowsOf(look).find((r) => r.id === match);
    setMeta((xs) => xs.map((x, idx) => {
      if (idx === i) return { ...x, match, taken: deal ? deal.extensions_taken ?? 0 : x.taken, eventLabel: deal?.event_label ?? milestones[i]?.event_label ?? x.eventLabel };
      return match !== NEW_ROW && x.match === match ? { ...x, match: NEW_ROW } : x;
    }));
    // A deal row picked here leaves the removal list; freed again later, it starts unticked.
    if (match !== NEW_ROW) setRemoveTicks((t) => { const next = { ...t }; delete next[match]; return next; });
  };
  /** What a row counts from, in words - the reviewer checks the chain against the clause below it. */
  const countsFrom = (m: ContractMilestone): string | null => {
    if (m.explicit_date || m.offset_days == null) return null;
    const unit = m.day_unit === 'business' ? 'business days' : 'days';
    if (m.offset_from === 'milestone' && m.after_milestone) {
      const j = meta.findIndex((x) => x.key.trim().toLowerCase() === m.after_milestone!.trim().toLowerCase());
      const name = j >= 0 ? milestones[j]?.label ?? m.after_milestone : m.after_milestone;
      const end = m.from_initial_end ? `${name}'s initial period ends` : `${name} ends`;
      return m.starts_day_after ? `${m.offset_days} ${unit}, beginning the day after ${end}` : `${m.offset_days} ${unit} after ${end}`;
    }
    if (m.offset_from === 'escrow_open') return `${m.offset_days} ${unit} from Day 1 (escrow open)`;
    if (m.offset_from === 'effective_date') return `${m.offset_days} ${unit} from Day 1 (the effective date)`;
    if (m.offset_from === 'contingency_removal') return `${m.offset_days} ${unit} after the contingencies are removed`;
    if (m.offset_from === 'agreement_date') return `${m.offset_days} ${unit} from the agreement's date`;
    if (m.offset_from === 'amendment_date') return `${m.offset_days} ${unit} from the amendment's date`;
    if (m.offset_from === null && m.event_label) return `${m.offset_days} ${unit} after ${m.event_label}`;
    return null;
  };
  const patchParty = (i: number, p: Partial<PartyRow>) => setParties((ps) => ps.map((row, idx) => idx === i ? { ...row, ...p } : row));

  // Sanity check: Close of Escrow should come after every contingency. ISO dates
  // compare lexicographically. Catches a misread "close after contingencies" date.
  const CONTINGENCY_TYPES = ['due_diligence', 'loan', 'appraisal', 'title'];
  const closingDate = milestones.map((m, i) => (m.deadline_type === 'closing' ? dateAt(i) : '')).filter(Boolean).sort()[0] || '';
  const latestContingencyDate = milestones.map((m, i) => (CONTINGENCY_TYPES.includes(m.deadline_type) ? dateAt(i) : '')).filter(Boolean).sort().slice(-1)[0] || '';
  const coeBeforeContingency = !!closingDate && !!latestContingencyDate && closingDate < latestContingencyDate;

  const dealMoney = (key: keyof ContractMoney) => look?.money_now.find((x) => x.field === key) ?? null;
  /** The line's tick: "Apply" while it has a figure, "Clear" while it is empty and the deal has one, none otherwise. */
  function moneyTick(key: keyof ContractMoney): 'Apply' | 'Clear' | null {
    return money[key] != null ? 'Apply' : dealMoney(key)?.value != null ? 'Clear' : null;
  }
  /** An amendment's read carries no money (its reader reads none): one line says so instead of four "does not state it". */
  const amendmentNoMoney = readKind === 'amendment' && MONEY_KEYS.every((k) => readMoney[k] == null);
  /** "you set $50,000 - the contract says $25,000" - only when the deal's figure differs from the contract's. */
  const moneyBeside = (key: keyof ContractMoney): string | null => {
    const now = dealMoney(key);
    if (!now || now.value == null || now.value === readMoney[key]) return null;
    const read = readMoney[key];
    if (read == null && readKind === 'amendment') return null;
    const who = now.set_by === 'you' ? 'you set' : now.set_by ? `${now.set_by} set` : 'the deal has';
    return `${who} ${moneyWords(key, now.value)} - ${read != null ? `the contract says ${moneyWords(key, read)}` : 'the contract does not state it'}`;
  };
  /**
   * The review's fix: a tick follows its line. Emptying a line turns its "Apply" into a "Clear" he has not ticked (never an
   * erase he did not choose); typing a figure into an empty line turns it into an "Apply", ticked (he typed it).
   */
  const editMoney = (key: keyof ContractMoney, raw: string) => {
    const value = numOrNull(raw);
    const before = moneyTick(key);
    const after = value != null ? 'Apply' : dealMoney(key)?.value != null ? 'Clear' : null;
    setMoney((mm) => ({ ...mm, [key]: value }));
    if (after !== before) setMoneyTicks((t) => ({ ...t, [key]: after === 'Apply' }));
  };
  const moneyField = (label: string, key: keyof ContractMoney, prefix: string) => {
    const tick = moneyTick(key);
    const beside = moneyBeside(key);
    return (
      <div className="space-y-0.5" data-testid="money-line">
        <div className="flex items-center gap-2">
          {/* The box says what it does: "Apply" writes the figure; "Clear" empties the deal's. */}
          {tick ? (
            <label className="flex items-center gap-1 text-[11px] text-gray-500">
              <input type="checkbox" aria-label={`${tick} ${label}`} checked={moneyTicks[key]} disabled={busy}
                onChange={(e) => setMoneyTicks((t) => ({ ...t, [key]: e.target.checked }))} />
              <span>{tick}</span>
            </label>
          ) : <span className="w-[13px]" />}
          <span className="text-xs text-gray-500 flex-1">{label}</span>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">{prefix}</span>
            <input type="text" inputMode="decimal" value={money[key] ?? ''} onChange={(e) => editMoney(key, e.target.value)}
              aria-label={label} className="w-28 border rounded px-2 py-1 text-sm text-right tabular-nums" placeholder="-" />
          </div>
        </div>
        {beside && <p className="pl-5 text-[11px] text-warning">{beside}</p>}
      </div>
    );
  };

  return (
    /* OO5d: through the Dialog primitive. */
    <Dialog
      open
      onClose={onClose}
      title="Set up from contract"
      size="xl"
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        {/* DOCTRACK: once Apply wrote the deadlines, it cannot run again while he picks a broker. */}
        {/* DOCTRACK part 2: Apply waits for the deal's first look, and is spent once the rows are written. */}
        <Button size="sm" onClick={apply} disabled={busy || !extracted || asks.length > 0 || !look || notMarked}>Apply</Button>
      </>}
    >
        <div>
          <p className="text-xs text-gray-500">Upload the PSA, review the deadlines, money, and parties, then apply. Nothing saves until you click Apply.</p>
        </div>
        <div className="p-4 space-y-4">
          {err && <div className="text-sm text-red-600">{err}</div>}
          {notMarked && <div className="text-sm text-warning">{NOT_MARKED_APPLIED}</div>}
          {extracted && lookFailed && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-warning">
              <span>{DEADLINES_UNLOADED}</span>
              <button onClick={tryAgain} disabled={busy} className="px-2 py-0.5 border rounded text-xs disabled:opacity-50">Try again</button>
            </div>
          )}
          {asks.length > 0 && (
            <section className="space-y-2 rounded-lg border border-warning/30 bg-warning-wash p-3" aria-label="Brokers to link">
              <p className="text-xs text-gray-600">The deadlines are applied. These brokers matched a contact by name only - pick the right one:</p>
              {asks.map((a, i) => (
                <div key={i} className="space-y-1">
                  <p className="text-sm text-gray-800">{a.result.name} ({ROLE_LABEL[a.result.role as ContractPartyRole] ?? a.result.role}): {NAME_ONLY}</p>
                  <div className="flex flex-wrap gap-2">
                    {(a.result.candidates ?? []).map((c) => (
                      <Button key={c.contact_id} size="sm" variant="outline" disabled={busy} onClick={() => answerAsk(i, { link_contact_id: c.contact_id })}>
                        {`Link ${c.name} (${c.firm || 'no brokerage on file'})`}
                      </Button>
                    ))}
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => answerAsk(i, { create: true })}>Create a new contact</Button>
                  </div>
                </div>
              ))}
              {askErr && <p className="text-xs text-red-600">{askErr}</p>}
            </section>
          )}
          {savedDoc && <div className="text-xs text-success">Saved &ldquo;{savedDoc}&rdquo; to this deal&rsquo;s Documents.</div>}
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600">Contract PDF</label>
            <input type="file" accept="application/pdf" onChange={onFile} disabled={busy} className="text-sm" />
            {busy && <span className="text-xs text-gray-400">Reading contract...</span>}
          </div>
          <label className="flex items-center gap-2 text-[13px] text-gray-600">
            <input type="checkbox" checked={asAmendment} onChange={(e) => setAsAmendment(e.target.checked)} disabled={busy} />
            Read as an amendment
          </label>
          {existingDocs.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">or use a document on this deal</label>
              <select value={pickedDoc} onChange={(e) => setPickedDoc(e.target.value)} disabled={busy} className="flex-1 min-w-0 border rounded px-2 py-1 text-sm">
                <option value="">Select a document…</option>
                {existingDocs.map((d) => <option key={d.id} value={d.id}>{d.file_name}</option>)}
              </select>
              <button onClick={onExtractExisting} disabled={busy || !pickedDoc} className="px-3 py-1 border rounded text-sm disabled:opacity-50 whitespace-nowrap">Extract</button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 flex-1">
              Escrow open date (Day 1) <span className="text-primary-500">*</span>
              {extracted && !anchor && needsDay1 && <span className="ml-2 text-[11px] text-primary-500">required to apply</span>}
            </label>
            {/* The review's fix: an amendment never sets Day 1 (part 1's guard, kept on the server), so the box shows the
                deal's own and cannot take a date that would be dropped. */}
            <input type="date" aria-label="Escrow open date (Day 1)" value={anchor} readOnly={readKind === 'amendment'}
              onChange={(e) => { if (readKind !== 'amendment') { setAnchor(e.target.value); setAnchorTyped(true); } }}
              className={`border rounded px-2 py-1 text-sm ${readKind === 'amendment' ? 'bg-gray-50 text-gray-500' : ''} ${extracted && !anchor && needsDay1 ? 'border-primary-200 ring-1 ring-primary-200' : ''}`} />
          </div>
          {extracted && readKind === 'amendment' && !anchor && needsDay1 && (
            <p className="text-[11px] text-warning">An amendment never sets Day 1 - set the escrow open date on the Deadlines tab first.</p>
          )}
          {extracted && showAmendmentDate && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 flex-1">{"This amendment's date"}</label>
              <input type="date" aria-label="This amendment's date" value={amendmentDate} onChange={(e) => setAmendmentDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </div>
          )}

          {extracted && (
            <>
              {(reused && readAt) || filingId === null || readKind === 'amendment' ? (
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  {reused && readAt && <span>{readOnWords(readAt)}</span>}
                  {reused && lastSource && (
                    <button onClick={() => read(lastSource, true)} disabled={busy} className="px-2 py-0.5 border rounded text-xs disabled:opacity-50">Read again</button>
                  )}
                  {filingId === null && <span className="text-warning">This read was not kept, so applying it will not be recorded.</span>}
                  {readKind === 'amendment' && <span>Read as an amendment: only the dates it changes are listed. Day 1 stays the deal's escrow open date - the amendment's own date never replaces it.</span>}
                </div>
              ) : null}
              {/* Money */}
              <section className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Money</p>
                {amendmentNoMoney && <p className="text-[11px] text-gray-500">{"This amendment's read carries no money - tick Clear only for a term it removes."}</p>}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {moneyField('Purchase price', 'purchase_price', '$')}
                  {moneyField('Earnest money (EMD)', 'emd_amount', '$')}
                  {moneyField('Loan amount', 'loan_amount', '$')}
                  {moneyField('Commission', 'commission_percent', '%')}
                </div>
              </section>

              {/* Parties */}
              <section className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Parties</p>
                {parties.length === 0 && <p className="text-sm text-gray-400">No parties found. Add them on the deal manually.</p>}
                {parties.map((p, i) => (
                  <div key={i} className="border rounded-lg p-2 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={p.link} onChange={(e) => patchParty(i, { link: e.target.checked })} title="Link this party to the deal" />
                      <select value={p.role} onChange={(e) => patchParty(i, { role: e.target.value as ContractPartyRole })} className="shrink-0 border rounded py-1 pl-2 pr-8 text-sm">
                        {PARTY_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                      <input value={p.name} onChange={(e) => patchParty(i, { name: e.target.value })} placeholder="Name" className="flex-1 border rounded px-2 py-1 text-sm" />
                    </div>
                    <div className="flex items-center gap-2 pl-6">
                      <input value={p.email} onChange={(e) => patchParty(i, { email: e.target.value })} placeholder="email@..." className="flex-1 border rounded px-2 py-1 text-sm" />
                      <label className="text-[11px] text-gray-500 flex items-center gap-1 whitespace-nowrap" title="If this matches an existing contact whose name differs, update it to the contract's name">
                        <input type="checkbox" checked={p.correct_name} onChange={(e) => patchParty(i, { correct_name: e.target.checked })} /> fix name
                      </label>
                    </div>
                    {isBroker(p.role) && (
                      <div className="flex items-center gap-2 pl-6">
                        <input value={p.firm} onChange={(e) => patchParty(i, { firm: e.target.value })} placeholder="Brokerage" aria-label={`Brokerage: ${p.name || 'broker'}`} className="flex-1 border rounded px-2 py-1 text-sm" />
                        <input value={p.phone} onChange={(e) => patchParty(i, { phone: e.target.value })} placeholder="Phone" aria-label={`Phone: ${p.name || 'broker'}`} className="w-40 border rounded px-2 py-1 text-sm" />
                      </div>
                    )}
                  </div>
                ))}
              </section>

              {/* Property */}
              {(property.address || property.apn) && (
                <section className="space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Property</p>
                  <p className="text-[13px] text-gray-600">{property.address || 'No address'}{property.apn ? ` · APN ${property.apn}` : ''}</p>
                  {propertyMatch ? (
                    <label className="flex items-center gap-2 text-[13px] text-gray-700">
                      <input type="checkbox" checked={linkProperty} onChange={(e) => setLinkProperty(e.target.checked)} />
                      Link to <b>{propertyMatch.address || 'matched property'}</b> in your database
                      <span className="text-[11px] text-success">{propertyMatch.method === 'apn_exact' ? 'APN match' : `${Math.round(propertyMatch.confidence * 100)}% address match`}</span>
                    </label>
                  ) : property.address ? (
                    <label className="flex items-center gap-2 text-[13px] text-gray-700">
                      <input type="checkbox" checked={linkProperty} onChange={(e) => setLinkProperty(e.target.checked)} />
                      Not in your database — <b>create a new property record</b> and link it
                    </label>
                  ) : null}
                </section>
              )}

              {/* Deadlines */}
              <section className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Deadlines</p>
                {coeBeforeContingency && (
                  <div className="text-xs rounded-lg bg-warning-wash border border-warning/30 text-warning px-3 py-2">
                    Close of Escrow ({closingDate}) lands <b>before</b> a contingency ({latestContingencyDate}). If the contract closes &ldquo;X days after contingencies are waived,&rdquo; increase the closing days so it falls after them.
                  </div>
                )}
                {/* ⚠️ CR1-B's review: the reader can miss the clause, so he can switch it; and only FEDERAL holidays are
                    known - a contract naming state holidays (the 1101 PSA: CA or North Carolina) needs his eye. */}
                <label className="flex items-center gap-2 text-[11px] text-gray-500">
                  <input type="checkbox" checked={weekendRoll} onChange={(e) => setWeekendRoll(e.target.checked)} />
                  Roll a period ending on a weekend or a federal holiday to the next business day{weekendRoll ? ' (the contract says so - state holidays are not counted)' : ''}
                </label>
                {milestones.length === 0 && <p className="text-sm text-gray-400">No milestones extracted. Add them manually or close.</p>}
                {milestones.map((m, i) => {
                  const r = resolved[i];
                  const from = countsFrom(m);
                  const opt = m.extension_options;
                  // DOCTRACK part 2: the deal row this row changes, as the deal holds it now.
                  const deal = contractRowsOf(look).find((d) => d.id === meta[i]?.match) ?? null;
                  const dealDate = deal?.due_date ? deal.due_date.slice(0, 10) : null;
                  const differs = !!deal && (deal.label !== m.label || dealDate !== (r?.date ?? null));
                  return (
                  <div key={i} data-testid="milestone-row" className="border rounded-lg p-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <input value={m.label} onChange={(e) => patch(i, { label: e.target.value })} className="flex-1 min-w-0 border rounded px-2 py-1 text-sm" />
                      {/* CR1-B's review: days typed on a STATED date count from escrow open; clearing the box keeps the date. */}
                      <input type="number" value={m.offset_days ?? ''} onChange={(e) => patch(i, e.target.value === ''
                        ? { offset_days: null }
                        : { offset_days: parseInt(e.target.value, 10), explicit_date: null, offset_from: m.explicit_date ? 'escrow_open' : m.offset_from })} placeholder="days" className="w-20 border rounded px-2 py-1 text-sm" />
                      {/* ⛔ CR1-B: a row with nothing to count from says so - it is never placed from escrow open. */}
                      <span data-testid="milestone-date" className={`text-xs text-right whitespace-nowrap ${r?.date ? 'text-gray-500 w-20' : 'text-warning font-medium'}`}>{r?.date ?? (r?.needs ? 'needs a date' : '-')}</span>
                      <button onClick={() => removeRow(i)} className="text-gray-300 hover:text-red-500 text-xs">x</button>
                    </div>
                    {(from || opt) && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        {from && <span>{from}</span>}
                        {opt && (
                          <label className="flex items-center gap-1">
                            <span>{opt.count} extension{opt.count === 1 ? '' : 's'} of {opt.days} days{opt.fee != null ? `, $${opt.fee.toLocaleString('en-US')} each` : ''}{beforeEndWords('notice', opt.notice_days_before, opt.notice_day_unit)}{beforeEndWords('fee due', opt.fee_due_days_before, opt.notice_day_unit)} - taken</span>
                            <input
                              type="number" min={0} max={opt.count} value={meta[i]?.taken ?? 0}
                              aria-label={`Extensions taken: ${m.label}`}
                              onChange={(e) => setTaken(i, Math.max(0, Math.min(opt.count, parseInt(e.target.value || '0', 10) || 0)))}
                              className="w-12 border rounded px-1 py-0.5 text-[11px]"
                            />
                          </label>
                        )}
                      </div>
                    )}
                    {!r?.date && r?.needs && (
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-warning">
                        <span>{r.needs === 'needs the escrow open date' ? 'Set the escrow open date above.' : `${r.needs.replace(/^needs a date - /, 'No date: ')}.`}</span>
                        {r.needs !== 'needs the escrow open date' && (
                          <label className="flex items-center gap-1 text-gray-600">
                            or set the date
                            <input type="date" aria-label={`Set the date: ${m.label}`} onChange={(e) => { if (e.target.value) { patch(i, { explicit_date: e.target.value }); patchMeta(i, { anchor: null }); } }} className="border rounded px-1 py-0.5 text-[11px]" />
                          </label>
                        )}
                      </div>
                    )}
                    {!isChain(m) && (meta[i]?.anchor || r?.anchor) && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        <label className="flex items-center gap-1">
                          Counts from
                          <select aria-label={`Counts from: ${m.label}`} value={meta[i]?.anchor ?? r?.anchor ?? ''} disabled={busy}
                            onChange={(e) => patchMeta(i, { anchor: e.target.value as DeadlineAnchor })} className="border rounded px-1 py-0.5 text-[11px]">
                            {ANCHOR_WORDS.map(([value, words]) => <option key={value} value={value}>{words}</option>)}
                          </select>
                        </label>
                        {ownAnchorOf(m, meta[i]?.anchor) === 'event' && (
                          <label className="flex items-center gap-1">
                            What starts it
                            <input aria-label={`What starts it: ${m.label}`} value={meta[i]?.eventLabel ?? ''} disabled={busy}
                              onChange={(e) => patchMeta(i, { eventLabel: e.target.value })} placeholder="Title report" className="w-44 border rounded px-1 py-0.5 text-[11px]" />
                          </label>
                        )}
                      </div>
                    )}
                    {look && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        <label className="flex items-center gap-1">
                          Matches
                          <select aria-label={`Matches: ${m.label}`} value={meta[i]?.match ?? NEW_ROW} onChange={(e) => setMatch(i, e.target.value)} disabled={busy}
                            className="border rounded px-1 py-0.5 text-[11px] max-w-[18rem]">
                            <option value={NEW_ROW}>New row</option>
                            {contractRowsOf(look).map((d) => (
                              <option key={d.id} value={d.id}>{`${d.label} - ${deadlineDate(d.due_date) ?? 'no date'}${d.is_completed ? ' (done)' : ''}`}</option>
                            ))}
                          </select>
                        </label>
                        {differs && <span>{`was "${deal!.label}", ${deadlineDate(deal!.due_date) ?? 'no date'}`}</span>}
                        {deal?.is_completed && <span className="text-success">stays done</span>}
                        {deal && dealDate && r?.date && r.date < dealDate && <span className="text-warning">moves earlier - extensions taken?</span>}
                      </div>
                    )}
                    {m.verified === 'no' && <p className="text-[11px] font-medium text-warning">not found in the document - check it</p>}
                    {m.source_text && <p className="text-[11px] text-gray-400 truncate" title={m.source_text}>{m.source_text}</p>}
                  </div>
                  );
                })}
                {/* DOCTRACK part 2: a deal row this contract does not have stays unless he ticks it. */}
                {look && freed.length > 0 && (
                  <div className="space-y-1 rounded-lg border p-2" aria-label="Rows on the deal only">
                    <p className="text-[12px] font-medium text-gray-700">{readKind === 'amendment' ? 'On the deal, not in this amendment' : 'On the deal, not in this contract'}</p>
                    {readKind === 'amendment' && <p className="text-[11px] text-warning">Rows that count from a period this amendment moves are not moved here - check their dates.</p>}
                    {freed.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-[12px] text-gray-600">
                        <input type="checkbox" aria-label={`Remove: ${d.label}`} checked={removeTicks[d.id] === true}
                          onChange={(e) => setRemoveTicks((t) => ({ ...t, [d.id]: e.target.checked }))} disabled={busy} />
                        {`${d.label} - ${deadlineDate(d.due_date) ?? 'no date'}${d.is_completed ? ' (done)' : ''}`}
                      </label>
                    ))}
                    <p className="text-[11px] text-gray-500">Left unticked, it stays on the deal.</p>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
    </Dialog>
  );
}
